import { useTranslation } from "@sagot/i18n";
import { cn } from "@sagot/ui";
import { useFormatters } from "../shared/format";
import type { Activity } from "./lead-types";

const KNOWN = new Set(["created", "stage_changed", "assigned", "note", "call", "email", "sms", "summary", "synced"]);

export type NameOf = (userId: string | null | undefined) => string | null;

function useActivityText(nameOf: NameOf) {
  const { t } = useTranslation();
  return (activity: Activity): string => {
    const data = activity.data as Record<string, unknown>;
    const str = (key: string) => (typeof data[key] === "string" ? data[key] : "");
    switch (activity.type) {
      case "created": {
        const channel = str("channel");
        return channel ? t("engage.leads.activityType.createdVia", { channel: t(`engage.channels.${channel}`, { defaultValue: channel }) }) : t("engage.leads.activityType.created");
      }
      case "stage_changed":
        return t("engage.leads.activityType.stage_changed", { to: str("to_stage") });
      case "assigned": {
        const owner = nameOf(str("owner_user_id"));
        return owner ? t("engage.leads.activityType.assigned", { name: owner }) : t("engage.leads.activityType.unassigned");
      }
      default:
        return KNOWN.has(activity.type) ? t(`engage.leads.activityType.${activity.type}`) : t("engage.leads.activityType.other");
    }
  };
}

/** Lead activity timeline (everything except notes, which have their own list). */
export function LeadActivityTimeline({ activities, nameOf }: { activities: readonly Activity[]; nameOf: NameOf }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const text = useActivityText(nameOf);
  const items = activities.filter((a) => a.type !== "note").sort((a, b) => a.created_at.localeCompare(b.created_at));
  return (
    <section aria-labelledby="lead-activity-title">
      <h3 id="lead-activity-title" className="label">
        {t("engage.leads.sheet.activity")}
      </h3>
      <ol className="relative space-y-3 border-l border-line pl-4 text-sm">
        {items.map((activity, index) => {
          const actor = nameOf(activity.actor_user_id);
          return (
            <li key={activity.id}>
              <span aria-hidden="true" className={cn("absolute -left-1 mt-1.5 size-2 rounded-pill", index === items.length - 1 ? "bg-primary" : "bg-ink-400")} />
              <div className="font-semibold">
                {text(activity)}
                {actor ? <span className="font-normal text-fg-muted"> {t("engage.leads.activityType.by", { name: actor })}</span> : null}
              </div>
              {activity.body && activity.type !== "created" ? <div className="text-fg-muted">{activity.body}</div> : null}
              <div className="text-xs text-fg-subtle">{format.dateTime(activity.created_at)}</div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
